/**
 * 请求方法，只开放三个
 * */
export type FDHttpMethod = 'GET' | 'PUT' | 'POST'
/**
 * 请求头值类型
 * */
export type FDHeaderValue = string | string[] | number | boolean | null
/**
 * 原始请求头类型
 * */
export interface FDRawHeaders {
  [key: string]: FDHeaderValue
}

// #ifdef H5
import type { FDHostInterface } from 'fdmer'
import { useUserStore } from '@/core/services/auth-credentials'
// #endif

/**
 * 请求选项
 * */
export interface FDRequestOptions<TQuery extends Record<string, any>, TData = any> {
  /**
   * 超时时间
   */
  timeout?: number
  /**
   * 基础路径
   */
  baseUrl?: string

  /**
   * 请求路径
   * */
  url?: string
  /**
   * 请求方法
   * */
  method?: FDHttpMethod

  /**
   * query 参数
   * */
  query?: TQuery

  /**
   * body
   * */
  data?: TData

  /**
   * 请求头
   */
  headers?: FDRawHeaders

  /**
   * 文件流
   */
  filePath?: any
}

/**
 * 响应
 * */
export interface FDResponse<TQuery extends Record<string, any>, TReqData = any, TRespData = any> {
  /**
   * 请求
   * */
  request: FDRequestOptions<TQuery, TReqData>
  /**
   * 响应数据
   * */
  data: TRespData
  /**
   * 状态码
   * */
  status: number
  /**
   * 响应头
   */
  headers?: FDRawHeaders
}

class FDHttpClient {
  /**
   * 基础请求参数
   * */
  protected baseOptions: UniNamespace.RequestOptions

  /**
   * 构造函数
   * */
  constructor(options: UniNamespace.RequestOptions) {
    this.baseOptions = options
  }

  public request<TQuery extends Record<string, any>, TReqData = any, TRespData = any>(
    options: FDRequestOptions<TQuery, TReqData>,
  ): Promise<FDResponse<TQuery, TReqData, TRespData>> {
    if (!options.url?.startsWith('http')) {
      options.url = options.baseUrl! + options.url
    }

    return new Promise<FDResponse<TQuery, TReqData, TRespData>>((resolve, reject) => {
      const requestOptions: UniNamespace.RequestOptions = {
        ...this.baseOptions,
        ...options,
        // 响应成功
        success(result: UniNamespace.RequestSuccessCallbackResult) {
          const resp: FDResponse<TQuery, TReqData, TRespData> = {
            request: options,
            status: result.statusCode,
            headers: result.header,
            data: result.data as TRespData,
          }
          const data = resp.data as any
          const code = data.code?.toString()
          const des = data.desc || data.message || data.msg

          if (
            code == '401' ||
            code == '100401' ||
            ((code == '400' || code === '0') && des.indexOf('请登录') !== -1)
          ) {
            // #ifdef H5
            const { userAgent } = navigator
            const isApp = userAgent.toLowerCase().indexOf('kuailife_app') !== -1
            if (isApp || window.KLifeUserApp) {
              uni.hideLoading()
              const title = code === '0' ? '登录信息已过期，请重新登录' : des
              uni.showToast({
                title,
                icon: 'none',
              })
              // 清空登录状态
              const { clearProfile } = useUserStore()
              clearProfile()

              setTimeout(() => {
                if (window.KLifeUserApp) {
                  window.KLifeUserApp.nav.login()
                } else {
                  const fdHost: FDHostInterface = window.getFDHostInstance()
                  fdHost.sendMessage({ cmd: '107' })
                }
              }, 500)
              return
            }
            // #endif

            if (getCurrentPages()[0].route === '/me/passport/login') {
              uni.showToast({
                title: des,
                icon: 'none',
              })
              return
            } else {
              uni.navigateTo({ url: '/me/passport/login' })
              reject(resp)
              return
            }
          }

          if (resp.status >= 200 && resp.status < 300) {
            resolve(resp)
            return
          }

          reject(resp)
        },
        fail(err: UniNamespace.GeneralCallbackResult) {
          // #ifdef H5
          const isGroupon = window.location.pathname.split('/')[1] === 'grouponPages' ? true : false
          if (isGroupon) {
            uni.navigateTo({
              url: '/networkErr/index',
            })
          }
          // #endif

          uni.hideLoading()

          reject(err.errMsg)
        },
        complete() {
          uni.hideLoading()
        },
      }

      requestOptions.header = {
        ...this.baseOptions.header,
        ...options.headers,
      }

      uni.request(requestOptions)
    })
  }

  public async get<TQuery extends Record<string, any>, TRespData = any>(
    url: string,
    params?: any,
    headers?: any,
  ): Promise<TRespData> {
    const resp = await this.request<TQuery, any, TRespData>({
      url,
      method: 'GET',
      query: {
        ...params,
      },
      headers: headers,
    })
    return resp.data
  }

  public upload<TQuery extends Record<string, any>, TReqData = any, TRespData = any>(
    options: FDRequestOptions<TQuery, TReqData>,
  ): Promise<FDResponse<TQuery, TReqData, TRespData>> {
    if (!options.url?.startsWith('http')) {
      options.url = options.baseUrl! + options.url
    }

    return new Promise<FDResponse<TQuery, TReqData, TRespData>>((resolve, reject) => {
      const requestOptions: UniNamespace.UploadFileOption = {
        url: options.url!,
        filePath: options.filePath,
        name: 'file',
        formData: options.data,
        success(result: UniNamespace.UploadFileSuccessCallbackResult) {
          if (result.statusCode === 200) {
            const data = JSON.parse(result.data)
            resolve(data)
            return
          }
          reject(result.errMsg)
        },
        fail(err: UniNamespace.GeneralCallbackResult) {
          uni.showToast({
            icon: 'none',
            title: err.errMsg,
          })
          reject(err.errMsg)
        },
      }
      uni.uploadFile(requestOptions)
    })
  }
}

/**
 * 默认 http客户端
 * */
const http = new FDHttpClient({
  url: '',
  timeout: 3000,
  enableHttp2: true,
  sslVerify: false,
  header: {},
  // 是否跨域携带 Cookie；生产接口 CORS 目前不支持 credentials，需通过环境变量关闭。
  withCredentials: import.meta.env.VITE_WITH_CREDENTIALS === 'true',
})

export default http
